import TWEEN from '@tweenjs/tween.js';
import Logger from '../Logger';
import WiredChatEffect from "./Effects/WiredChatEffect";
import ElectricityEffect from "./Effects/ElectricityEffect";

import * as THREE from "three";
import {
    AmbientLight,
    BoxGeometry,
    Fog,
    Mesh,
    MeshPhongMaterial,
    PerspectiveCamera,
    PointLight,
    Scene,
    WebGLRenderer
} from "three";
import {Theme} from "@mui/material/styles";
import tinycolor from "tinycolor2";
import {Message} from "../stores/MessageStore";

const logger = new Logger("WiredScene");

export class WiredScene {
    renderArea: HTMLElement | null;
    renderer: WebGLRenderer & { id: string };
    private wiredEffect: WiredChatEffect | null;
    private electricityEffect: ElectricityEffect | null;
    private readonly id: string;
    private readonly name: string;
    private running: boolean;
    private background: THREE.Color;
    private dark: THREE.Color;
    private main: THREE.Color;

    constructor({id}: { id: string }) {
        this.wiredEffect = null;
        this.electricityEffect = null;
        this.id = id;
        this.name = "WiredScene";
        this.renderArea = null;
        (window as any).ws = this;
    }


    onMessage(message: Message) {
        if (!this.running) {
            throw new Error("Tried to add effect but scene isn't running.")
        }

        const effects = [
            {
                name: "wg",
                effect: this.wiredEffect,
            },
            {
                name: "ds",
                effect: this.electricityEffect,
                weight: 0,
            },
        ].map(e => ({...e, weight: e.weight === undefined ? 1 : e.weight}))

        const nr = Math.random();
        const totalWeight = effects.map(e => e.weight).reduce((acc, v) => acc + (v as number), 0);
        let currentWeight = 0;
        const scaledNr = nr * totalWeight;
        let chosenEffect;

        for (const ef of effects) {
            currentWeight += (ef.weight as number);
            if (scaledNr < currentWeight) {
                chosenEffect = ef;
                break;
            }
        }

        if (chosenEffect) {
            chosenEffect.effect.run(message);
        }

    }


    async run() {
        if (this.running) {
            throw new Error("already running");
        }

        logger.trace("running " + this.name);
        const renderArea = this.renderArea;
        let width = renderArea!.clientWidth;
        let height = renderArea!.clientHeight;

        const camera = new PerspectiveCamera(70, width / height, 0.01, 30000);
        camera.position.z = 1;
        const scene = new Scene();

        this.wiredEffect = new WiredChatEffect(scene);
        this.electricityEffect = new ElectricityEffect(scene);
        await this.wiredEffect.init();
        await this.electricityEffect.init();

        scene.fog = new Fog(0xffffff, 0.015, 10000);
        const geometry = new BoxGeometry(0.2, 0.2, 0.2);

        const meshMaterial = new MeshPhongMaterial();
        const mesh = new Mesh(geometry, meshMaterial);
        mesh.position.setX(1);
        mesh.position.setZ(-1);

        const light1 = new PointLight();
        // light1.intensity = 1000;
        light1.position.set(0, 0, 0);

        scene.add(light1);

        // mesh.material.color.setHex( 0xffffff );
        // scene.add( mesh );
        const light = new AmbientLight(0x404040, 150); // soft white light
        scene.add(light);
        const renderer = new WebGLRenderer({antialias: true});

        renderer.setSize(width, height);
        renderArea!.appendChild(renderer.domElement);

        logger.trace("running animation loop");

        const animation = (time: number) => {
            TWEEN.update(time)

            this.wiredEffect!.material.color = this.dark;
            this.wiredEffect!.materials[0].color = this.main
            this.wiredEffect!.materials[1].color = this.main
            scene.background = this.background
            scene.fog.color = this.background
            meshMaterial.color = this.main

            const width = renderArea!.clientWidth;
            const height = renderArea!.clientHeight;

            camera.aspect = width / height;
            camera.updateProjectionMatrix();
            renderer.setSize(width, height);
            renderer.render(scene, camera);

        }

        renderer.setAnimationLoop(animation);
        // @ts-ignore
        this.renderer = renderer;
        this.renderer.id = this.id;
        this.running = true;
    }

    close() {
        this.running = false;
        this.renderer.setAnimationLoop(null);
    }

    syncTheme(theme: Theme) {
        const mainString = tinycolor(theme.palette.primary.main).toRgbString();
        const darkString = tinycolor(theme.palette.primary.dark).toRgbString();

        this.main = new THREE.Color(mainString);
        this.dark = new THREE.Color(darkString);
        this.background = new THREE.Color(tinycolor(theme.palette.background.default).toRgbString());

    }
}
